/**
 * "2 days ago", "last week" — the phrasing the prototype uses on session rows.
 *
 * Deliberately coarse. Nobody needs to know a workshop ran 47 minutes ago, and a
 * precise timestamp reads as machine output next to "last week".
 */
export function relativeTime(timestampMs: number | null, now: number = Date.now()): string {
  // A pending server timestamp reads as null until acknowledged.
  if (timestampMs === null) return "just now"

  const seconds = Math.round((now - timestampMs) / 1000)
  if (seconds < 90) return "just now"

  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} minutes ago`

  const hours = Math.round(minutes / 60)
  if (hours < 24) return hours === 1 ? "an hour ago" : `${hours} hours ago`

  const days = Math.round(hours / 24)
  if (days === 1) return "yesterday"
  if (days < 7) return `${days} days ago`

  const weeks = Math.round(days / 7)
  if (weeks === 1) return "last week"
  // Stops at three: a fourth week is how people say "last month".
  if (weeks < 4) return `${weeks} weeks ago`

  const months = Math.round(days / 30)
  if (months === 1) return "last month"
  if (months < 12) return `${months} months ago`

  const years = Math.round(days / 365)
  return years === 1 ? "last year" : `${years} years ago`
}

/**
 * "48 min", "1 hr 5 min" — a finished session's total run time, coarse like
 * `relativeTime`.
 *
 * `startedAt`/`completedAt` are the session's own lifecycle stamps, not the
 * per-round timer: the timer resets every round change, so it cannot answer
 * "how long did the whole workshop run".
 */
export function runDuration(startedAt: number | null, completedAt: number | null): string {
  if (startedAt === null || completedAt === null) return "—"
  const minutes = Math.max(Math.round((completedAt - startedAt) / 60_000), 1)
  if (minutes <= 59) return `${minutes} min`

  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  return remainder === 0 ? `${hours} hr` : `${hours} hr ${remainder} min`
}

/**
 * "20 Feb 2026" — an absolute date, for a table column.
 *
 * A row in a list of thirty is being scanned and compared, not read as prose, so
 * "3 weeks ago" beside "last month" makes two rows harder to order than two
 * dates do. Cards keep `relativeTime`, where there is one date and no comparison.
 *
 * A pending server timestamp reads as null until acknowledged, and a date that
 * has not been assigned yet is honestly a dash rather than today.
 */
export function shortDate(timestampMs: number | null): string {
  if (timestampMs === null) return "—"
  return new Date(timestampMs).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}
